<template>
  <div v-if="!isAdminPage && !isModalOpen">
    <a
      :href="whatsAppLink"
      target="_blank"
      rel="noopener noreferrer"
      class="whatsapp-bubble fixed end-4 z-50 flex aspect-square size-[60px] items-center justify-center rounded-full safe-area-bottom"
      style="background-color: #25d366"
      :aria-label="ariaLabel"
    >
      <!-- Tooltip -->
      <span class="whatsapp-tooltip">{{ tooltip }}</span>

      <!-- WhatsApp logo SVG -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        class="size-[32px]"
        fill="white"
        aria-hidden="true"
      >
        <path
          d="M16.002 2C8.28 2 2 8.28 2 16.002c0 2.478.651 4.804 1.787 6.82L2 30l7.363-1.76A13.93 13.93 0 0 0 16.002 30C23.722 30 30 23.72 30 16.002 30 8.28 23.722 2 16.002 2zm0 25.45a11.4 11.4 0 0 1-5.817-1.592l-.417-.248-4.368 1.045 1.066-4.258-.273-.437A11.41 11.41 0 0 1 4.55 16.002c0-6.316 5.137-11.452 11.452-11.452 6.316 0 11.452 5.136 11.452 11.452 0 6.315-5.136 11.448-11.452 11.448zm6.281-8.57c-.344-.172-2.035-1.004-2.35-1.118-.315-.115-.545-.172-.774.172-.23.344-.888 1.118-1.088 1.348-.2.23-.4.258-.744.086-.344-.172-1.453-.536-2.768-1.71-1.023-.913-1.713-2.04-1.913-2.384-.2-.344-.021-.53.15-.701.155-.154.344-.402.516-.602.172-.2.23-.344.344-.573.115-.23.058-.43-.029-.602-.086-.172-.774-1.864-1.06-2.554-.279-.67-.562-.578-.774-.588l-.659-.011c-.23 0-.602.086-.917.43-.315.344-1.203 1.176-1.203 2.868s1.231 3.326 1.403 3.556c.172.23 2.422 3.699 5.871 5.188.82.354 1.46.566 1.96.724.824.262 1.574.225 2.167.137.661-.099 2.035-.831 2.322-1.634.287-.803.287-1.492.201-1.634-.086-.143-.315-.23-.659-.402z"
        />
      </svg>
    </a>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useCurrentAccountManager } from '@/api/queries/accountManagers';
import { stripLocalePrefix } from '~/utils/i18n/locale';

const { t } = useI18n();
const route = useRoute();
const uiStore = useUIStore();
const { isModalOpen } = storeToRefs(uiStore);

const { data: accountManagerData } = useCurrentAccountManager();
// The account manager only resolves client-side — use it after mount so SSR and the
// first client render match (no hydration mismatch on the label/link).
const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;
});
const accountManager = computed(() => (isMounted.value ? accountManagerData.value : null));

const DEFAULT_WHATSAPP = '971527033506';

const isAdminPage = computed(() => {
  const path = stripLocalePrefix(route.path);
  return (
    path.includes('/admin') ||
    path.includes('/signup') ||
    path.includes('/login') ||
    path.includes('/reset') ||
    path.startsWith('/masterclass')
  );
});

const amFirstName = computed(() => accountManager.value?.firstName || null);
const ariaLabel = computed(() =>
  amFirstName.value
    ? t('shell.openAgentInfo.ariaLabel', { name: amFirstName.value })
    : t('shell.openAgentInfo.ariaLabelTeam'),
);
const tooltip = computed(() =>
  amFirstName.value
    ? t('shell.openAgentInfo.tooltip', { name: amFirstName.value })
    : t('shell.openAgentInfo.tooltipTeam'),
);

// Prefill goes to the account manager / ThriveState team (internal ops) — kept in English on purpose.
const whatsAppLink = computed(() => {
  const rawPhone = accountManager.value?.phone;
  const phone = rawPhone ? rawPhone.replace(/\D/g, '') : DEFAULT_WHATSAPP;
  const name = accountManager.value?.firstName || 'the team';
  const text = encodeURIComponent(`Hello ${name}, I would like to get more information.`);
  return `https://wa.me/${phone}?text=${text}`;
});
</script>

<style scoped>
.safe-area-bottom {
  bottom: calc(5rem + env(safe-area-inset-bottom, 0px));
}

@media (min-width: 1024px) {
  .safe-area-bottom {
    bottom: 1.5rem;
  }
}

.whatsapp-bubble {
  box-shadow: 0 4px 16px rgba(37, 211, 102, 0.4);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.whatsapp-bubble:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.55);
}

/* Tooltip */
.whatsapp-tooltip {
  position: absolute;
  bottom: calc(100% + 10px);
  inset-inline-end: 0;
  white-space: nowrap;
  background: #1a1a1a;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 8px;
  pointer-events: none;
  opacity: 0;
  transform: translateY(4px);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.whatsapp-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  inset-inline-end: 20px;
  border: 5px solid transparent;
  border-top-color: #1a1a1a;
}

.whatsapp-bubble:hover .whatsapp-tooltip {
  opacity: 1;
  transform: translateY(0);
}
</style>
